import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useMappedState } from 'redux-react-hook';
import _isBoolean from 'lodash/isBoolean';
import _isEmpty from 'lodash/isEmpty';
import { TreatmentSearch } from 'components/treatmentSearch';
import { ConstructedNavigationBar as NavigationBar } from 'styles/blocks/navigationBar';
import {
  getTreatmentCategoryWithCodeName,
  setPractice,
  setFederatedLoginProvider,
  setEncounterId,
  getNextQuestion,
  getPreviousQuestion,
  getNextQuestionAfterLogin,
  addAllMultipleValidation,
  setTwoWayMessageId,
} from 'redux/actions/consultation';
import {
  confirmContinueWithConsult,
  confirmLeavingPage,
  openLeavePageWarning,
  restartSession,
  backToTreatmentCategoryListing,
  setSessionExpiryValidation,
} from 'redux/actions/session';
import { setIsClinicalReviewMode, clearShowSessionExpiryValidationOnLoad, setForceNhsLoginMode } from 'redux/actions/flags';
import { updateLoginStatus, setTestCode } from 'redux/actions/login';
import { Consultation } from 'components/consultation';
import { ColumnDiv } from 'styles/elements/div';
import { getModals } from 'components/consultation/modal';
import { getPublicHealthAlert } from 'redux/actions/consultation/healthAlert';
import { sendQuestionToGoogleAnalytics } from 'common/googleAnalytics';

export function PrimaryCareContainer({
  treatmentCategoryCodeNameAsProps,
  isClinicalReviewModeAsProps,
  federatedLoginProviderAsProps,
  logInStatusAsProps,
  testCodeAsProps,
  practiceAsProps,
  encounterIdAsProps,
  healthAlertUrlAsProps,
  product,
  twoWayMessageId,
  forceNhsLoginModeAsProps,
}) {
  // Use this container as a higher order component
  const mapState = useCallback(state => ({
    questionIndex: state.consultationReducer.questionIndex,
    treatmentCategory: state.consultationReducer.treatmentCategory,
    isClinicalReviewMode: state.flagsReducer.isClinicalReviewMode,
    apiVersion: state.consultationReducer.apiVersion,
    practice: state.consultationReducer.practice,
    question: state.consultationReducer.question,
    responseType: state.consultationReducer.responseType,
    validationErrors: state.consultationReducer.validationErrors,
    previous: state.consultationReducer.previous,
    next: state.consultationReducer.next,
    isAttemptingToLeavePage: state.consultationReducer.isAttemptingToLeavePage,
    submissionDetails: state.consultationReducer.submissionDetails,
    TEST_CODE: state.loginReducer.TEST_CODE,
    loginDetails: state.consultationReducer.loginDetails,
    showSessionExpiryValidationOnLoad: state.flagsReducer.showSessionExpiryValidationOnLoad,
  }), []);
  const {
    loginDetails,
    questionIndex,
    treatmentCategory,
    isClinicalReviewMode,
    apiVersion,
    practice,
    question,
    responseType,
    validationErrors,
    previous,
    next,
    isAttemptingToLeavePage,
    submissionDetails,
    TEST_CODE,
    showSessionExpiryValidationOnLoad,
  } = useMappedState(mapState);
  // Create actions
  const dispatch = useDispatch();

  const { LeavingPageWarningModal } = useMemo(() => getModals(product), [product]);

  const handleBrowserNavigationPopState = useCallback(event => {
    const isRealLocationInHistoryStack = event.state === null || event.state.questionIndex === null;
    const isForwardNavigation = !isRealLocationInHistoryStack && event.state.questionIndex > questionIndex;
    const isBackwardNavigation = !isRealLocationInHistoryStack && event.state.questionIndex < questionIndex;
    const isDisclaimersQuestion = questionIndex === 0;
    const isLoggedIn = loginDetails !== null && loginDetails.isLoggedIn;
    if (isRealLocationInHistoryStack && isDisclaimersQuestion && showSessionExpiryValidationOnLoad) {
      dispatch(clearShowSessionExpiryValidationOnLoad());
    }
    if (isRealLocationInHistoryStack && !isDisclaimersQuestion) {
      if (!previous) {
        dispatch(openLeavePageWarning());
      } else {
        dispatch(getPreviousQuestion());
      }
    } else if (isForwardNavigation) {
      if (isLoggedIn) {
        dispatch(getNextQuestionAfterLogin(loginDetails));
      } else if (!isDisclaimersQuestion) {
        dispatch(getNextQuestion());
      } else if (isDisclaimersQuestion) {
        // We cannot control the user's use of the browser back/forward. Sending them back with validation is the best method we've come up with.
        // This triggers a loop while event.state.questionIndex > questionIndex.
        window.history.back();
        dispatch(addAllMultipleValidation());
      }
    } else if (isBackwardNavigation) {
      if (previous && isLoggedIn) {
        dispatch(confirmLeavingPage());
        window.history.go(-event.state.questionIndex);
      } else if (previous) {
        dispatch(getPreviousQuestion());
      } else {
        dispatch(openLeavePageWarning());
      }
    }
  }, [previous, questionIndex, loginDetails, dispatch, showSessionExpiryValidationOnLoad]);

  const handleBrowserNavigationOnLoad = () => {
    if (window.history.state !== null && window.history.state.questionIndex !== null && questionIndex !== 0) {
      const index = window.history.state.questionIndex;
      if (index < questionIndex) {
        dispatch(getPreviousQuestion());
      } else if (index > questionIndex) {
        if (loginDetails !== null && loginDetails.isLoggedIn) {
          dispatch(getNextQuestionAfterLogin(loginDetails));
        } else {
          dispatch(getNextQuestion());
        }
      }
    } else if (window.history.state !== null && window.history.state.questionIndex !== null && questionIndex === 0) {
      // User has back-stepped into an ended consult.
      // history.back() here would trigger a loop where it will be repeatedly called by handleBrowserNavigationPopState while window.history.state > 0.
      // go back X amount of steps to questionIndex 0 (fires once). Jumps through the history to the privacy/first question.
      if (showSessionExpiryValidationOnLoad) {
        dispatch(setSessionExpiryValidation(product));
      }
      window.history.go(0 - window.history.state.questionIndex);
    }
  };

  useEffect(() => {
    window.onpopstate = handleBrowserNavigationPopState;
  }, [questionIndex, handleBrowserNavigationPopState]);
  useEffect(() => {
    window.onload = handleBrowserNavigationOnLoad;
  });
  // Any time the question changes, attempt to send a GA pageView event. This relies on the global ga variable being set by the main webapp
  useEffect(() => {
    const isValidationError = validationErrors && validationErrors.length > 0;
    const isSuccessfulSubmit = !_isEmpty(submissionDetails);
    sendQuestionToGoogleAnalytics(treatmentCategory, question.questionId, isValidationError, isSuccessfulSubmit, responseType);
  }, [responseType, treatmentCategory, question, validationErrors, submissionDetails]);

  useEffect(() => {
    if ((!practice.practiceId || !practice.odsCode) && practiceAsProps && practiceAsProps.practiceId && practiceAsProps.odsCode) {
      dispatch(setPractice(practiceAsProps));
    }
    if (federatedLoginProviderAsProps) {
      dispatch(setFederatedLoginProvider(federatedLoginProviderAsProps));
    }
  }, [dispatch, practiceAsProps, federatedLoginProviderAsProps, practice]);

  useEffect(() => {
    if (_isBoolean(forceNhsLoginModeAsProps)) {
      dispatch(setForceNhsLoginMode(forceNhsLoginModeAsProps));
    }
    if (_isBoolean(isClinicalReviewModeAsProps)) {
      dispatch(setIsClinicalReviewMode(isClinicalReviewModeAsProps));
    }
    if (encounterIdAsProps) {
      dispatch(setEncounterId(encounterIdAsProps));
    }
  }, [dispatch, isClinicalReviewModeAsProps, encounterIdAsProps, forceNhsLoginModeAsProps]);

  useEffect(() => {
    if (practice.practiceId && practice.odsCode) {
      if (treatmentCategoryCodeNameAsProps && !treatmentCategory) {
        dispatch(getPublicHealthAlert(healthAlertUrlAsProps));
        dispatch(getTreatmentCategoryWithCodeName(treatmentCategoryCodeNameAsProps));
      } else if (treatmentCategoryCodeNameAsProps && (treatmentCategoryCodeNameAsProps !== treatmentCategory)) {
        dispatch(restartSession());
      }
    }
  }, [dispatch, treatmentCategory, treatmentCategoryCodeNameAsProps, practice, healthAlertUrlAsProps]);

  useEffect(() => {
    if (logInStatusAsProps) {
      dispatch(updateLoginStatus(logInStatusAsProps));
    }
  }, [dispatch, logInStatusAsProps]);
  useEffect(() => {
    if (testCodeAsProps !== TEST_CODE) {
      dispatch(setTestCode(testCodeAsProps));
    }
  }, [dispatch, testCodeAsProps, TEST_CODE]);
  useEffect(() => {
    if (isAttemptingToLeavePage && !_isBoolean(previous)) {
      dispatch(confirmLeavingPage());
    }
  }, [isAttemptingToLeavePage, dispatch, previous]);
  useEffect(() => {
    if (twoWayMessageId) {
      dispatch(setTwoWayMessageId(twoWayMessageId));
    }
  }, [twoWayMessageId, dispatch]);

  const consultationBody = () => {
    if (!treatmentCategory && !treatmentCategoryCodeNameAsProps) {
      return <TreatmentSearch />;
    }
    return (
      <Consultation
        isClinicalReviewMode={isClinicalReviewMode}
        treatmentCategoryCodeNameAsProps={treatmentCategoryCodeNameAsProps}
        isUserLoggedIn={logInStatusAsProps.isLoggedIn}
        product={product}
      />
    );
  };

  return (
    <ColumnDiv modifier={['flexFill']}>
      {!treatmentCategoryCodeNameAsProps && (
        <NavigationBar
          onButtonClick={() => dispatch(backToTreatmentCategoryListing())}
          isClinicalReviewMode={isClinicalReviewMode}
          apiVersion={apiVersion}
        />
      )}
      {consultationBody()}
      <LeavingPageWarningModal
        isOpen={isAttemptingToLeavePage && _isBoolean(previous) && next}
        onSelectConfirm={() => dispatch(confirmContinueWithConsult())}
        onSelectCancel={() => dispatch(confirmLeavingPage())}
      />
    </ColumnDiv>
  );
}
